import React from "react";
import PropTypes from "prop-types";
import { TextBoxComponent } from "@syncfusion/ej2-react-inputs";
import { TooltipComponent } from "@syncfusion/ej2-react-popups";
import { PiQuestion } from "react-icons/pi";
import classNames from "classnames";

/**
 * TextInput — Syncfusion TextBoxComponent wrapper with project-standard styling.
 *
 * Uses the outline variant with floating labels for a modern Material-like
 * appearance. Integrates with react-hook-form via Controller's `value`/`onChange`.
 *
 * Pass `hint` to show a small **?** icon on the top-right border of the
 * input, aligned with the floating label. The label is forced to "Always"
 * so both sit on the border line together. Tooltip appears on hover.
 *
 * @example
 * <TextInput placeholder="First Name *" value={field.value} onChange={field.onChange} error={errors.firstName?.message} />
 *
 * @example
 * // With hint tooltip
 * <TextInput placeholder="Org ID" value={v} onChange={fn} disabled hint="Used by team members to find your org." />
 */
export default function TextInput({
  placeholder,
  value,
  onChange,
  error,
  hint,
  readOnly = false,
  disabled = false,
  className,
  floatLabelType = "Auto",
  ...props
}) {
  return (
    <div className={classNames("text-input-wrapper", className)}>
      <TooltipComponent
        content={hint ?? ""}
        position="TopCenter"
        openDelay={100}
        cssClass="e-tooltip-wrap"
        target=".hint-icon-target"
        opensOn="Hover"
      >
        <div className="relative">
          <TextBoxComponent
            placeholder={placeholder}
            floatLabelType={hint ? "Always" : floatLabelType}
            value={value ?? ""}
            input={e => onChange?.(e.value)}
            readonly={readOnly}
            enabled={!disabled}
            cssClass={classNames("e-outline", { "e-error": !!error })}
            {...props}
          />
          {hint && (
            <span className="hint-icon-target absolute right-0 top-2 z-10 inline-flex -translate-y-1/2 cursor-help bg-white px-0.5 text-gray-400 transition-colors hover:text-indigo-500 dark:bg-slate-900 dark:text-slate-500 dark:hover:text-indigo-400">
              <PiQuestion className="size-4" aria-hidden="true" />
            </span>
          )}
        </div>
      </TooltipComponent>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

TextInput.propTypes = {
  /** Floating-label / placeholder text */
  placeholder: PropTypes.string,
  /** Controlled value */
  value: PropTypes.string,
  /** Change handler — receives the raw string value */
  onChange: PropTypes.func,
  /** Validation error message */
  error: PropTypes.string,
  /** Tooltip hint text — renders a ? icon on the top-right border, aligned with the label */
  hint: PropTypes.string,
  /** Render as read-only (focusable but not editable) */
  readOnly: PropTypes.bool,
  /** Fully disabled (greyed-out, not focusable) */
  disabled: PropTypes.bool,
  /** Additional CSS classes for the wrapper */
  className: PropTypes.string,
  /** Syncfusion float-label type */
  floatLabelType: PropTypes.oneOf(["Auto", "Always", "Never"]),
};
